import { IconApproved, IconCross, IconTrash } from 'assets/img';
import { DateTime } from 'luxon';
import { useAcceptShiftApplication } from 'ogp/hooks/queryHooks/shiftApplications/useAcceptShiftApplication';
import {
  AcknowledgeButton,
  PrimaryButton,
  SecondaryButton,
  useDialog,
} from 'ogp/providers/dialog-provider';
import { ContextMenuItem } from 'redesign';
import { shiftAlerts, useAlert } from 'shared/components/alerts';
import { useInvalidateQuery } from 'shared/hooks/query-hooks/useInvalidateQuery';
import { useDenyShiftApplication } from 'ogp/hooks/queryHooks/shiftApplications/useDenyShiftApplication';
import { useBulkAcceptApplicationsDialog } from 'shared/components/shift-detail-modal/components/use-bulk-accept-shift-applications-dialog/use-bulk-accept-shift-applications-dialog';
import { ApplicantDetail } from 'shared/components/shift-detail-modal/components/tabs/applicants/applicant-table.types';
import { QUERY_KEYS } from 'api/query-keys';
import { useOgpBulkAcceptShiftApplications } from '../../../query-hooks/ogp/use-ogp-bulk-accept-shift-applications';

const useOgpGetApplicantActions = () => {
  const { openDialog, closeDialog, disableDialog, enableDialog } = useDialog();
  const { alertSuccess, alertError } = useAlert();
  const { open: openBulkAcceptShiftApplications, Element: BulkAcceptShiftApplicationsDialog } =
    useBulkAcceptApplicationsDialog(useOgpBulkAcceptShiftApplications);
  const invalidateQuery = useInvalidateQuery();
  const acceptShiftApplication = useAcceptShiftApplication();
  const denyShiftApplication = useDenyShiftApplication();

  const handleDenyShiftApplication = (
    shiftApplicationId: string,
    flexWorkerId: string,
    shiftId: string
  ) => {
    openDialog(
      {
        title: 'Weet je zeker dat je deze aanmelding wilt afwijzen?',
        secondaryButton: (props) => (
          <PrimaryButton startIcon={<IconCross />} {...props}>
            Annuleer
          </PrimaryButton>
        ),
        primaryButton: (props) => (
          <SecondaryButton startIcon={<IconApproved />} iconColorMode="stroke" {...props}>
            Wijs af
          </SecondaryButton>
        ),
      },
      () => {
        disableDialog();
        denyShiftApplication.mutate(
          { shiftApplicationId, flexWorkerId },
          {
            onSuccess: () => {
              invalidateQuery(QUERY_KEYS.shifts());
              invalidateQuery(QUERY_KEYS.shift(shiftId));
              closeDialog();
              alertSuccess(shiftAlerts.success.unsubscribed);
            },
            onError: (e) => alertError(e),
            onSettled: () => {
              enableDialog();
            },
          }
        );
      }
    );
  };

  const handleAcceptShiftApplication = ({ shiftId, workerId, applicationId }: ApplicantDetail) => {
    openDialog(
      {
        title: 'De aanmelding mag geaccepteerd worden?',
      },
      () => {
        disableDialog();
        acceptShiftApplication.mutate(
          {
            shiftId: shiftId,
            flexWorkerId: workerId,
            shiftApplicationId: applicationId,
          },
          {
            onSuccess: () => {
              invalidateQuery(QUERY_KEYS.shifts());
              invalidateQuery(QUERY_KEYS.shift(shiftId));
              closeDialog();
              alertSuccess(shiftAlerts.success.subscribed);
            },
            onError: (e) => alertError(e),
            onSettled: () => {
              enableDialog();
            },
          }
        );
      }
    );
  };

  const handleAccept = (application: ApplicantDetail, shiftStartDateTime: string) => {
    const isShiftInThePast = DateTime.fromISO(shiftStartDateTime).toUTC() < DateTime.now().toUTC();
    if (isShiftInThePast) {
      openDialog(
        {
          title:
            'Oops! Deze shift bevindt zich in het verleden, accepteren van aanmeldingen is niet mogelijk.',
          secondaryButton: (props) => <AcknowledgeButton {...props} />,
          primaryButton: null,
        },
        () => {}
      );

      return;
    }

    if (application.allApplications.length === 1) {
      handleAcceptShiftApplication(application);
    } else {
      openBulkAcceptShiftApplications(
        { id: application.workerId, firstName: application.firstName },
        application.applicationId,
        application.allApplications
      );
    }
  };

  const getApplicantActions = (
    application: ApplicantDetail,
    shiftStartDateTime: string,
    options?: { employmentTypeMismatch?: boolean }
  ) => [
    <ContextMenuItem
      key="accept"
      disabled={options?.employmentTypeMismatch}
      onClick={() => handleAccept(application, shiftStartDateTime)}
    >
      <IconApproved /> Accepteren
    </ContextMenuItem>,
    <ContextMenuItem
      key="deny"
      onClick={() =>
        handleDenyShiftApplication(
          application.applicationId,
          application.workerId,
          application.shiftId
        )
      }
    >
      <IconTrash />
      Afwijzen
    </ContextMenuItem>,
  ];

  return { getApplicantActions, BulkAcceptShiftApplicationsDialog };
};
export { useOgpGetApplicantActions };
